import React, { useState } from 'react';
import apiFetch, { defaultApiFetchErrorHandler } from 'react-scale/lib/api-fetch';
import { Modal, TextField, Button, SelectMenu } from '@scale/scaleui';
import SelectScaler from 'react-scale/corp/components/UI/SelectScaler';
import { ReviewLevel } from '@scale/shared-types';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

interface IClaimUser {
  label: string;
  value: string;
}

const DEFAULT_CLAIM_USER: IClaimUser = {
  label: '',
  value: '',
};

const ReassignSubstageClaimModal = ({
  currentWorkerEmail,
  substageInstanceId,
  reviewLevel,
  open,
  setOpen,
  title,
}: {
  currentWorkerEmail: string | undefined;
  substageInstanceId: string;
  reviewLevel: ReviewLevel | undefined;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
}) => {
  const { isRemoAdmin } = useRemoAdminCtx();

  const [newClaimUser, setNewClaimUser] = useState<IClaimUser>(DEFAULT_CLAIM_USER);
  const [payPerHour, setPayPerHour] = useState<number>(0);
  const [maxPayPerSubstage, setMaxPayPerSubstage] = useState<number>(0);
  const [isPayingOutLastWorker, setIsPayingOutLastWorker] = useState<boolean>(false);
  const [maxClaim, setMaxClaim] = useState<boolean>(true);

  const handleReassignClaimSubmission = async () => {
    try {
      const reassignmentMessage = await apiFetch(null, `corp-api/sfu_task_tracker/reassign_claim`, {
        method: 'POST',
        body: {
          substageInstanceId,
          taskerEmail: newClaimUser.label,
          reviewLevel,
          maxClaim,
          isPayingOutLastWorker,
          payPerHour,
          maxPayPerSubstage,
        },
      });
      alert(reassignmentMessage);
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
  };

  const handleSubmit = () => {
    handleReassignClaimSubmission();
    setOpen(false);
    setNewClaimUser(DEFAULT_CLAIM_USER);
    setPayPerHour(0);
    setMaxPayPerSubstage(0);
  };

  return (
    <div className="scaleui scale-tailwind">
      <Modal
        innerClassName="max-w-sm"
        onClose={() => setOpen(false)}
        theme="scale-theme"
        title={title}
        open={open}
        actions={
          <div className="flex items-center gap-2 justify-end">
            <Button onClick={handleSubmit}>Submit</Button>
          </div>
        }
      >
        <div className="py-2 mr-30">
          <strong>Current worker claim: </strong>
          <span>{currentWorkerEmail || 'None'}</span>
        </div>
        <div className="py-2">
          <strong>Select a new tasker for claim</strong>
          <SelectScaler
            placeholder="Select a scale user to assign this task"
            value={newClaimUser}
            onChange={setNewClaimUser}
            filterOption={() => true}
          />
        </div>
        <div className="py-2">
          <SelectMenu
            className="w-48"
            helperText="Please select whether or not you would like to maximize the tasker claim time"
            label="Set Max Claim"
            options={[
              {
                id: 'Yes',
                name: 'Yes',
              },
              {
                id: 'No',
                name: 'No',
              },
            ]}
            selectedOptionId={maxClaim ? 'Yes' : 'No'}
            setSelected={value => setMaxClaim(value === 'Yes')}
          />
        </div>

        {!isRemoAdmin && currentWorkerEmail && (
          <div className="py-2">
            <SelectMenu
              className="w-48"
              helperText="Please select whether or not you would like to payout the last tasker with claim"
              label="Pay Out Last Tasker"
              options={[
                {
                  id: 'Yes',
                  name: 'Yes',
                },
                {
                  id: 'No',
                  name: 'No',
                },
              ]}
              selectedOptionId={isPayingOutLastWorker ? 'Yes' : 'No'}
              setSelected={value => setIsPayingOutLastWorker(value === 'Yes')}
            />
          </div>
        )}

        {isPayingOutLastWorker && (
          <div>
            <div className="py-2 mr-30">
              <TextField
                className="w-72 py-2"
                id="text-field"
                label="Pay per hour (in USD), ex. 0.53"
                onChange={e => {
                  if (/^[0-9]+(\.[0-9]+)?$/.test(e.target.value)) {
                    setPayPerHour(parseFloat(e.target.value));
                  }
                }}
                placeholder="0"
                type="number"
                value={payPerHour}
              />
            </div>
            <div className="py-2 mr-30">
              <TextField
                className="w-72 py-2"
                id="text-field"
                label="Max pay per substage, ex: 10.32"
                onChange={e => {
                  if (/^[0-9]+(\.[0-9]+)?$/.test(e.target.value)) {
                    setMaxPayPerSubstage(parseFloat(e.target.value));
                  }
                }}
                placeholder="0"
                type="number"
                value={maxPayPerSubstage}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
export default ReassignSubstageClaimModal;
